import React, { useState } from 'react';
import { useModal } from '../../contexts/ModalContext';
import { jobsApi, clientsApi, certificatesApi } from '../../utils/api';
import MultiStepModal from './MultiStepModal';
import CompletionStatus from './CompletionStatus';
import JobDetailsStep from './steps/JobDetailsStep';
import QuoteDetailsStep from './steps/QuoteDetailsStep';
import AssignEngineerStep from './steps/AssignEngineerStep';
import CustomerInfoStep from './steps/CustomerInfoStep';
import AddSiteStep from './steps/AddSiteStep';
import ReviewStep from './steps/ReviewStep';
import JobTypeStep from './steps/JobTypeStep';
import JobClientSiteStep from './steps/JobClientSiteStep';
import JobDetailsStep2 from './steps/JobDetailsStep2';
import JobScheduleStep from './steps/JobScheduleStep';
import JobPricingStep from './steps/JobPricingStep';
import JobNotesStep from './steps/JobNotesStep';
import JobConfirmStep from './steps/JobConfirmStep';
import InvoiceReviewStep from './steps/InvoiceReviewStep';
import CertificateCreateModeStep from './steps/CertificateCreateModeStep';
import CertificateRequestReviewStep from './steps/CertificateRequestReviewStep';
import CertificateRequestSiteJobStep from './steps/CertificateRequestSiteJobStep';
import CertificateTemplateMetaStep from './steps/CertificateTemplateMetaStep';
import CertificateTemplateUploadStep from './steps/CertificateTemplateUploadStep';
import CertificateTemplatePreviewGroupStep from './steps/CertificateTemplatePreviewGroupStep';
import CertificateTemplateFieldsStep from './steps/CertificateTemplateFieldsStep';
import CertificateTemplateConfirmStep from './steps/CertificateTemplateConfirmStep';
import CertificateTemplateSelectStep from './steps/CertificateTemplateSelectStep';
import ClientIdentityStep from './steps/ClientIdentityStep';
import ClientContactStep from './steps/ClientContactStep';
import ClientAddressStep from './steps/ClientAddressStep';
import ClientReviewStep from './steps/ClientReviewStep';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../hooks/useData';
import { useToast } from '../../contexts/ToastContext';

export default function ModalManager() {
  const { isOpen, modalType, modalProps, closeModal } = useModal();
  const [showCompletion, setShowCompletion] = useState(false);
  const [completionTitle, setCompletionTitle] = useState('');
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const handleJobComplete = async (data: any) => {
    // Create new client if needed
    let clientId = data.customerId;
    if (data.isNewCustomer) {
      const created = await clientsApi.create({
        name: data.customerName,
        email: data.customerEmail || '',
        phone: data.customerPhone,
        address: data.customerAddress,
        postcode: data.postcode || '',
      });
      const payload = created.data;
      clientId = payload?.data?.id || payload?.id;
      if (!clientId) throw new Error('Failed to create client');
    }

    const scheduledIso = data.scheduledDate && data.scheduledTime
      ? new Date(`${data.scheduledDate}T${data.scheduledTime}`).toISOString()
      : undefined;

    const jobData: any = {
      title: data.title,
      jobType: data.jobType || undefined,
      description: [
        data.description,
        data.typeField_0,
        data.typeField_1,
      ].filter(Boolean).join('\n') || '',
      clientId,
      assignedEngineerId: data.engineerId || undefined,
      scheduledDate: scheduledIso,
      priority: data.priority || 'MEDIUM',
      address: data.siteAddress || data.customerAddress || '',
      postcode: data.sitePostcode || data.postcode || '',
      estimatedDuration: data.estimatedDuration || 60,
      estimatedCost: data.estimatedCost || 0,
      estimatedRevenue: data.estimatedRevenue || 0,
      notes: data.notes || '',
    };

    if (data.siteId) jobData.siteId = data.siteId;

    await jobsApi.create(jobData);
    await queryClient.invalidateQueries({ queryKey: queryKeys.jobsAll });
    setCompletionTitle('Job');
    setShowCompletion(true);
    showToast('Job created successfully', 'success');
  };

  const handleCertificateRequestComplete = async (data: any) => {
    try {
      let clientId = data.customerId;

      if (data.isNewCustomer) {
        const clientData = {
          name: data.customerName,
          email: data.customerEmail || '',
          phone: data.customerPhone,
          address: data.customerAddress,
          postcode: data.postcode || '',
          notes: data.notes || '',
        };

        const createdClient = await clientsApi.create(clientData);
        const clientPayload = createdClient.data;
        const client = clientPayload?.data || clientPayload;
        clientId = client?.id;
      }

      if (!clientId) {
        throw new Error('Missing client for certificate request');
      }

      const certificateKind = String((data as any)?.certificateKind || '').trim();
      const lockedCp17 = certificateKind === 'CP17_BREAKDOWN';

      const template = (data as any)?.selectedTemplate;
      const templateLabel = lockedCp17
        ? 'CP17 — Breakdown'
        : String(
            template?.templateLabel || template?.certificateTypeName || template?.certificatePrefix || 'Certificate',
          );
      const engineerId = String((data as any)?.engineerId || '').trim() || undefined;

      const scheduledDate = String((data as any)?.scheduledDate || '').trim();
      const scheduledTime = String((data as any)?.scheduledTime || '').trim();
      const scheduledIso = scheduledDate && scheduledTime ? new Date(`${scheduledDate}T${scheduledTime}`).toISOString() : undefined;
      if (!scheduledIso) throw new Error('Missing schedule for engineer');

      const jobNumber = String((data as any)?.jobNumber || '').trim();
      const siteName = String((data as any)?.siteName || '').trim();
      const siteAddress = String((data as any)?.siteAddress || '').trim();
      const sitePostcode = String((data as any)?.sitePostcode || '').trim();
      const address =
        siteAddress || String((data as any)?.customerAddress || '').trim() || '—';
      const postcode =
        sitePostcode || String((data as any)?.postcode || '').trim() || '';
      const instructions = String((data as any)?.instructions || '').trim();
      const templateId = String((data as any)?.templateId || '').trim();

      const metaBits = [
        jobNumber && `Job number: ${jobNumber}`,
        siteName && `Site name: ${siteName}`,
        siteAddress && `Site address: ${siteAddress}`,
        sitePostcode && `Site postcode: ${sitePostcode}`,
      ].filter(Boolean);

      const notesCombined = [instructions || '', metaBits.join('\n')].filter(Boolean).join('\n\n') || undefined;

      await jobsApi.create({
        title: jobNumber
          ? `Certificate: ${templateLabel} (${jobNumber})`
          : `Certificate request: ${templateLabel}`,
        description: [
          lockedCp17
            ? `Requested locked certificate: ${certificateKind} (bundled CP17 Breakdown Master PDF)`
            : `Requested certificate template: ${templateLabel}${templateId ? ` (templateId: ${templateId})` : ''}`,
          ...metaBits,
        ]
          .filter(Boolean)
          .join('\n'),
        clientId,
        assignedEngineerId: engineerId,
        scheduledDate: scheduledIso,
        address,
        postcode,
        notes: notesCombined,
        ...(lockedCp17 ? { certificateKind } : {}),
        ...(!lockedCp17 && templateId ? { certificateTemplateId: templateId } : {}),
      });

      showToast('Engineer request created', 'success');

      if (window.location.pathname.includes('/jobs')) {
        window.location.reload();
      }
    } catch (error: any) {
      console.error('Error requesting engineer:', error);
      alert(`Failed to request engineer: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleCertificateTemplateComplete = async (data: any) => {
    const certificatePrefix = String(data.certificatePrefix || '').trim();
    const certificateTypeName = String(data.certificateTypeName || '').trim();
    const templateLabel = String(data.templateLabel || '').trim();
    const pdfStoragePath = String(data.pdfStoragePath || '').trim();
    const fields = Array.isArray(data.fields) ? data.fields : [];

    if (!certificatePrefix || !certificateTypeName || !templateLabel || !pdfStoragePath) {
      throw new Error('Missing template details');
    }

    if (fields.length === 0) {
      throw new Error('No fields extracted');
    }

    await certificatesApi.createTemplate({
      certificatePrefix,
      certificateTypeName,
      templateLabel,
      pdfStoragePath,
      fields: fields.map((f: any, idx: number) => ({
        pdfFieldName: f.pdfFieldName,
        displayLabel: f.displayLabel,
        fieldType: f.fieldType,
        required: Boolean(f.required),
        hint: f.hint || undefined,
        groupName: f.groupName ? String(f.groupName) : undefined,
        orderIndex: typeof f.orderIndex === 'number' ? f.orderIndex : idx,
      })),
    });

    await queryClient.invalidateQueries({ queryKey: queryKeys.certificateTemplates });
    showToast('Certificate template saved', 'success');
  };

  const handleQuoteComplete = async (data: any) => {
    try {
      console.log('Creating quote with data:', data);
      
      // Transform modal data to API format
      const quoteData = {
        title: data.title,
        description: data.description || '',
        clientId: data.customerId, // This should come from customer step
        scheduledDate: data.scheduledDate ? new Date(`${data.scheduledDate}T${data.scheduledTime || '09:00'}`).toISOString() : null,
        priority: data.priority || 'MEDIUM',
        address: data.customerAddress || data.siteAddress,
        postcode: data.postcode || '',
        estimatedDuration: data.estimatedDuration || 60,
        estimatedCost: data.estimatedCost || 0,
        estimatedRevenue: data.estimatedRevenue || 0,
        notes: data.notes || '',
      };

      // For now, just log since quotes API might not exist
      console.log('Quote created successfully:', quoteData);
      
      setCompletionTitle('Quote');
      setShowCompletion(true);
      
      // Refresh the quotes page if we're on it
      if (window.location.pathname.includes('/quotes')) {
        window.location.reload();
      }
    } catch (error: any) {
      console.error('Error creating quote:', error);
      alert(`Failed to create quote: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleCompletionClose = () => {
    setShowCompletion(false);
    closeModal();
  };

  const renderModal = () => {
    console.log('ModalManager rendering:', { isOpen, modalType, modalProps });
    
    if (!isOpen) {
      console.log('Modal not open, returning null');
      return null;
    }
    
    console.log('Modal is open, checking modalType:', modalType);
    
    
    switch (modalType) {
      case 'createJob':
        return (
          <>
            <MultiStepModal
              isOpen={isOpen && !showCompletion}
              onClose={closeModal}
              title="New job"
              subtitle="Job intake wizard — type-driven, allocation-aware"
              steps={[
                {
                  id: 'jobType',
                  title: 'Job type',
                  component: JobTypeStep,
                  validation: (data) => !!data.jobType,
                },
                {
                  id: 'clientSite',
                  title: 'Client & site',
                  component: JobClientSiteStep,
                  validation: (data) => {
                    if (data.isNewCustomer) return !!(data.customerName?.trim() && data.customerPhone?.trim() && data.customerAddress?.trim());
                    return !!data.customerId;
                  },
                },
                {
                  id: 'jobDetails',
                  title: 'Job details',
                  component: JobDetailsStep2,
                  validation: (data) => !!data.title?.trim(),
                },
                {
                  id: 'schedule',
                  title: 'Schedule',
                  component: JobScheduleStep,
                  validation: (data) => !!(data.scheduledDate && data.scheduledTime),
                },
                {
                  id: 'pricing',
                  title: 'Pricing',
                  component: JobPricingStep,
                  validation: () => true,
                },
                {
                  id: 'notes',
                  title: 'Notes',
                  component: JobNotesStep,
                  validation: () => true,
                },
                {
                  id: 'confirm',
                  title: 'Confirm',
                  component: JobConfirmStep,
                  validation: () => true,
                },
              ]}
              onComplete={handleJobComplete}
              initialData={modalProps.initialData || {}}
            />
            <CompletionStatus
              isVisible={showCompletion}
              onClose={handleCompletionClose}
              onComplete={handleCompletionClose}
              title={completionTitle}
            />
          </>
        );

      case 'createSite':
        return (
          <MultiStepModal
            isOpen={isOpen}
            onClose={closeModal}
            title="Add site"
            subtitle="Create a new property record"
            steps={[
              {
                id: 'siteDetails',
                title: 'Site Details',
                component: AddSiteStep,
              },
            ]}
            onComplete={(data) => {
              console.log('Site created (mock):', data);
              closeModal();
            }}
            initialData={modalProps.initialData || {}}
          />
        );

      case 'createCertificateTemplate':
        return (
          <MultiStepModal
            isOpen={isOpen}
            onClose={closeModal}
            title="Add certificate template"
            subtitle="Upload a fillable PDF and configure its fields."
            steps={[
              {
                id: 'meta',
                title: 'Details',
                component: CertificateTemplateMetaStep,
                validation: (d) => {
                  if (!String(d.certificatePrefix || '').trim()) return false;
                  if (!String(d.certificateTypeName || '').trim()) return false;
                  if (!String(d.templateLabel || '').trim()) return false;
                  return true;
                },
              },
              {
                id: 'upload',
                title: 'Upload & Extract',
                component: CertificateTemplateUploadStep,
                validation: (d) => {
                  if (!d.templateFile) return false;
                  if (!String(d.pdfStoragePath || '').trim()) return false;
                  const f = Array.isArray(d.fields) ? d.fields : [];
                  return f.length > 0;
                },
              },
              {
                id: 'preview',
                title: 'Preview & Group',
                component: CertificateTemplatePreviewGroupStep,
              },
              {
                id: 'fields',
                title: 'Fields',
                component: CertificateTemplateFieldsStep,
                validation: (d) => {
                  const f = Array.isArray(d.fields) ? d.fields : [];
                  if (f.length === 0) return false;
                  return f.every((x: any) =>
                    String(x.pdfFieldName || '').trim() &&
                    String(x.displayLabel || '').trim() &&
                    String(x.fieldType || '').trim(),
                  );
                },
              },
              {
                id: 'confirm',
                title: 'Confirm',
                component: CertificateTemplateConfirmStep,
              },
            ]}
            onComplete={handleCertificateTemplateComplete}
            initialData={modalProps.initialData || {}}
          />
        );

      case 'createCertificate':
      case 'newCertificate':
        return (
          <MultiStepModal
            isOpen={isOpen}
            onClose={closeModal}
            title="Create New Certificate"
            subtitle="Generate a new safety certificate"
            steps={[
              {
                id: 'mode',
                title: 'Start',
                component: CertificateCreateModeStep,
                validation: (d) => Boolean(String((d as any)?.certificateFlow || '').trim()),
              },
              {
                id: 'templateSelect',
                title: 'Certificate type',
                component: CertificateTemplateSelectStep,
                validation: (d) => {
                  if (String((d as any)?.certificateFlow || '') === 'request') {
                    return String((d as any)?.certificateKind || '').trim() === 'CP17_BREAKDOWN';
                  }
                  return Boolean(String(d.templateId || '').trim());
                },
              },
              {
                id: 'siteJob',
                title: 'Site & job',
                component: CertificateRequestSiteJobStep,
                validation: (d) => {
                  if (String((d as any)?.certificateFlow || '') !== 'request') return true;
                  return (
                    Boolean(String((d as any).jobNumber || '').trim()) &&
                    Boolean(String((d as any).siteAddress || '').trim())
                  );
                },
              },
              {
                id: 'customerInfo',
                title: 'Customer Info',
                component: CustomerInfoStep,
                validation: (data) => {
                  if (data.isNewCustomer) {
                    return !!(data.customerName?.trim() && data.customerPhone?.trim() && data.customerAddress?.trim());
                  }
                  return !!data.customerId;
                }
              },
              {
                id: 'assignEngineer',
                title: 'Engineer',
                component: AssignEngineerStep,
                validation: (d) => {
                  if (!String((d as any)?.engineerId || '').trim()) return false;
                  if (!String((d as any)?.scheduledDate || '').trim()) return false;
                  if (!String((d as any)?.scheduledTime || '').trim()) return false;
                  return true;
                },
              },
              {
                id: 'reviewRequest',
                title: 'Review',
                component: CertificateRequestReviewStep,
              }
            ]}
            onComplete={handleCertificateRequestComplete}
          />
        );

      case 'addClient':
      case 'createClient':
        return (
          <MultiStepModal
            isOpen={isOpen}
            onClose={closeModal}
            title="Add client"
            subtitle="Create a rich client record in a few quick steps"
            submitLabel="Create client"
            steps={[
              {
                id: 'identity',
                title: 'Identity',
                component: ClientIdentityStep,
                validation: (d) => {
                  if (!String(d.customerName || '').trim()) return false;
                  const t = (d.clientType as string) || 'INDIVIDUAL';
                  if ((t === 'BUSINESS' || t === 'LETTING_AGENT') && !String(d.companyName || '').trim()) {
                    return false;
                  }
                  return true;
                },
              },
              {
                id: 'contact',
                title: 'Contact',
                component: ClientContactStep,
                validation: (d) => !!String(d.customerPhone || '').trim(),
              },
              {
                id: 'address',
                title: 'Address',
                component: ClientAddressStep,
                validation: (d) => !!String(d.customerAddress || '').trim(),
              },
              {
                id: 'review',
                title: 'Review',
                component: ClientReviewStep,
              },
            ]}
            onComplete={async (data) => {
              const displayName = String(data.customerName || '').trim();
              const clientType = (data.clientType as string) || 'INDIVIDUAL';
              const isBusiness = clientType === 'BUSINESS' || clientType === 'LETTING_AGENT';
              const billingSameAsPrimary = data.billingSameAsPrimary !== false;
              const billingEmail = billingSameAsPrimary
                ? String(data.customerEmail || '').trim() || undefined
                : String(data.billingEmail || '').trim() || undefined;

              const clientData: any = {
                name: isBusiness && data.companyName ? String(data.companyName).trim() : displayName,
                clientType,
                companyName: isBusiness ? String(data.companyName || '').trim() || undefined : undefined,
                email: String(data.customerEmail || '').trim() || undefined,
                phone: String(data.customerPhone || '').trim(),
                address: String(data.customerAddress || '').trim(),
                addressLine2: String(data.addressLine2 || '').trim() || undefined,
                city: String(data.city || '').trim() || undefined,
                postcode: String(data.postcode || '').trim() || undefined,
                country: String(data.country || 'United Kingdom').trim(),
                notes: String(data.notes || '').trim() || undefined,
                billingEmail,
                marketingOptIn: Boolean(data.marketingOptIn),
              };

              await clientsApi.create(clientData);
              await queryClient.invalidateQueries({ queryKey: queryKeys.clients });
              showToast('Client created', 'success');
            }}
            initialData={{
              clientType: 'INDIVIDUAL',
              country: 'United Kingdom',
              billingSameAsPrimary: true,
              marketingOptIn: false,
              ...(modalProps.initialData || {}),
            }}
          />
        );

      case 'createInvoice':
        return (
          <MultiStepModal
            isOpen={isOpen}
            onClose={closeModal}
            title="Create New Invoice"
            subtitle="Generate an invoice for completed work"
            className="invoice-modal"
            steps={[
              {
                id: 'invoiceDetails',
                title: 'Invoice Details',
                component: InvoiceReviewStep,
                validation: (data) => {
                  if (!data.invoiceNumber?.trim()) return false;
                  if (!data.description?.trim()) return false;
                  // Require at least one line item with a positive line total
                  const items = data.lineItems || [];
                  const hasValidLineItem = items.some((item: any) => {
                    const qty = parseFloat(item.quantity || '0') || 0;
                    const unit = parseFloat(item.unitPrice || '0') || 0;
                    return qty > 0 && unit > 0;
                  });
                  if (!hasValidLineItem) return false;
                  if (!data.invoiceDate) return false;
                  if (!data.dueDate) return false;

                  // Customer validation
                  if (data.isNewCustomer) {
                    return !!(
                      data.customerName?.trim() &&
                      data.customerPhone?.trim() &&
                      data.customerAddress?.trim()
                    );
                  }
                  return !!data.customerId;
                },
              },
            ]}
            onComplete={(data) => {
              console.log('Invoice created:', data);
              closeModal();
            }}
          />
        );

      case 'createQuote':
        return (
          <>
            <MultiStepModal
              isOpen={isOpen && !showCompletion}
              onClose={closeModal}
              title="Create New Quote"
              subtitle="Generate a quote for potential work"
              steps={[
                {
                  id: 'quoteDetails',
                  title: 'Quote Details',
                  component: QuoteDetailsStep,
                  validation: (data) => {
                    if (!data.title?.trim()) return false;
                    if (!data.type) return false;
                    if (!data.priority) return false;
                    return true;
                  }
                },
                {
                  id: 'customerInfo',
                  title: 'Customer Info',
                  component: CustomerInfoStep,
                  validation: (data) => {
                    if (data.isNewCustomer) {
                      return !!(data.customerName?.trim() && data.customerPhone?.trim() && data.customerAddress?.trim());
                    }
                    return !!data.customerId;
                  }
                },
                {
                  id: 'review',
                  title: 'Review & Send Quote',
                  component: ReviewStep
                }
              ]}
              onComplete={handleQuoteComplete}
              initialData={modalProps.initialData || {}}
            />
            <CompletionStatus
              isVisible={showCompletion}
              onClose={handleCompletionClose}
              onComplete={handleCompletionClose}
              title={completionTitle}
            />
          </>
        );

      case 'generateReport':
        return (
          <MultiStepModal
            isOpen={isOpen}
            onClose={closeModal}
            title="Generate Report"
            subtitle="Create a custom business report"
            steps={[
              {
                id: 'reportDetails',
                title: 'Report Configuration',
                component: JobDetailsStep, // Placeholder - would need specific report step
              }
            ]}
            onComplete={(data) => {
              console.log('Report generated:', data);
              closeModal();
            }}
          />
        );

      default:
        console.log('Unknown modal type:', modalType);
        return null;
    }
  };

  return <>{renderModal()}</>;
}
